import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { Lawsuit } from '@tvia-ai/shared';

const LawsuitManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [lawsuits, setLawsuits] = useState<Lawsuit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  const fetchLawsuits = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await axios.get<{
        data: Lawsuit[];
        pagination: { total: number; pages: number };
      }>(`/api/admin/lawsuits?${params.toString()}`);

      setLawsuits(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      console.error('Failed to fetch lawsuits:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLawsuits();
  }, [page, search, statusFilter, categoryFilter]);

  const handleStatusChange = async (lawsuitId: string, newStatus: string) => {
    try {
      await axios.put(`/api/admin/lawsuit/${lawsuitId}/status`, { status: newStatus });
      await fetchLawsuits();
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin.actionFailed', 'Action failed'));
    }
  };

  const handleDeleteLawsuit = async (lawsuitId: string) => {
    if (!confirm(t('admin.confirmDeleteLawsuit', 'Are you sure you want to delete this lawsuit?'))) {
      return;
    }

    try {
      await axios.delete(`/api/admin/lawsuit/${lawsuitId}`);
      await fetchLawsuits();
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin.deleteFailed', 'Delete failed'));
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await axios.get(
        `/api/admin/export/lawsuits${format === 'pdf' ? '/pdf' : ''}?${params.toString()}`,
        { responseType: 'blob' }
      );

      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `lawsuits-${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      alert(err.response?.data?.error || t('admin.exportFailed', 'Export failed'));
    }
  };

  if (isLoading && lawsuits.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('admin.lawsuitManagement', 'Lawsuit Management')}
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {t('admin.exportCSV', 'Export CSV')}
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {t('admin.exportPDF', 'Export PDF')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={t('admin.searchLawsuits', 'Search by title, user name, or email...')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.filterByStatus', 'Filter by Status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-white text-gray-900 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
            >
              <option value="">{t('admin.allStatuses', 'All Statuses')}</option>
              <option value="draft">Draft</option>
              <option value="analyzing">Analyzing</option>
              <option value="pending_payment">Pending Payment</option>
              <option value="submitted">Submitted</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('admin.filterByCategory', 'Filter by Category')}
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-white text-gray-900 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
            >
              <option value="">{t('admin.allCategories', 'All Categories')}</option>
              <option value="consumer_rights">Consumer Rights</option>
              <option value="employment">Employment</option>
              <option value="personal_injury">Personal Injury</option>
              <option value="contract_dispute">Contract Dispute</option>
              <option value="property">Property</option>
              <option value="family">Family</option>
              <option value="criminal">Criminal</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lawsuits Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.lawsuit', 'Lawsuit')}
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.category', 'Category')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.status', 'Status')}
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.created', 'Created')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lawsuits.map((lawsuit) => (
                <tr key={lawsuit.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{lawsuit.title}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {lawsuit.country}
                      <span className="sm:hidden ml-1">• {lawsuit.category.replace(/_/g, ' ')}</span>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">
                      {lawsuit.category.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <select
                      value={lawsuit.status}
                      onChange={(e) => handleStatusChange(lawsuit.id, e.target.value)}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-900 max-w-[130px]"
                    >
                      <option value="draft">Draft</option>
                      <option value="analyzing">Analyzing</option>
                      <option value="pending_payment">Pending Payment</option>
                      <option value="submitted">Submitted</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td className="hidden md:table-cell px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(lawsuit.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => window.open(`/lawsuit/${lawsuit.id}`, '_blank')}
                        className="text-cyan-600 hover:text-cyan-900"
                        title={t('admin.view', 'View')}
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteLawsuit(lawsuit.id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('admin.delete', 'Delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {lawsuits.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('admin.noLawsuits', 'No lawsuits found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {t('pagination.previous', 'Previous')}
          </button>
          <span className="text-sm text-gray-700 self-center">
            {page} / {Math.ceil(total / limit)}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {t('pagination.next', 'Next')}
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              {t('pagination.showing', 'Showing')}{' '}
              <span className="font-medium">{Math.min((page - 1) * limit + 1, total)}</span>{' '}
              {t('pagination.to', 'to')}{' '}
              <span className="font-medium">{Math.min(page * limit, total)}</span>{' '}
              {t('pagination.of', 'of')}{' '}
              <span className="font-medium">{total}</span>{' '}
              {t('pagination.results', 'results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pagination.previous', 'Previous')}
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {page}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(total / limit)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('pagination.next', 'Next')}
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LawsuitManagementPage;
